import { memo, useMemo, useEffect, useCallback } from 'react';
import { Box, Grid, Stack } from '@mui/material';
import DialogTextField from 'components/dialog/DialogTextField';
import useProjectInfoProvider from 'hooks/useProjectInfoProvider';
import useMenu from 'hooks/useMenu';
import { useProject } from 'hooks/useProject';
import { drawGridCondition } from 'libs/projectUtils';
import GridPoint from './GridPoint';
import GridPointBox from './GridPointBox';

function StepperDC2(): JSX.Element {
  const { getInspectionDetail } = useProject();
  const { dcInputs, onChangeDC } = useProjectInfoProvider();
  const { open } = useMenu();
  const { stepper1, stepper2, inspectionId } = dcInputs;
  const { ...others } = stepper2;
  const { data, isLoading } = getInspectionDetail(inspectionId || '');
  const gridDetail = useMemo(() => {
    return data?.grid || '0,0,0,0,0,0,0,0,0,0,0,0,0';
  }, [data?.grid]);

  const isUpdate = useMemo(
    () => !isLoading && gridDetail !== Object.values(others).join(','),
    [gridDetail, isLoading, others],
  );

  useEffect(() => {
    onChangeDC('stepper2Update', isUpdate);
  }, [isUpdate, onChangeDC]);

  const ipsativeRowCount = useMemo(() => {
    return data?.inspectionIpsatives?.length || stepper1.ipsativeExcel.rowCount || 0;
  }, [data?.inspectionIpsatives?.length, stepper1.ipsativeExcel.rowCount]);

  const handleCalcSum = useMemo(() => {
    return (
      Object.values(others)
        .map(obj => Number(obj))
        .reduce((acc, curr) => acc + curr, 0) || 0
    );
  }, [others]);
  const remainCount = useMemo(() => ipsativeRowCount - handleCalcSum, [handleCalcSum, ipsativeRowCount]);

  const updateStepperGrid = useCallback(() => {
    const gridValues = gridDetail.split(',');
    drawGridCondition.forEach((obj, index) => {
      onChangeDC(`stepper2.${obj.id}`, gridValues[index]);
    });
  }, [gridDetail, onChangeDC]);

  useEffect(() => {
    updateStepperGrid();
  }, [updateStepperGrid]);

  useEffect(() => {
    onChangeDC('stepper1.ipsativeExcel.rowCount', ipsativeRowCount);
  }, [ipsativeRowCount, onChangeDC]);

  return (
    <Grid item xs={12}>
      <Box sx={{ px: 0 }}>
        <Grid
          container
          spacing={2}
          sx={{
            mt: '1.5rem',
            ml: 0,
            width: '100%',
            display: 'flex',
            justifyContent: { xs: 'flex-start', lg: 'center' },
            minHeight: 'calc(100vh - 291px)',
          }}
        >
          <Grid item xs={5} md={3} sx={{ pl: '0px!important', pt: '0px!important' }}>
            <DialogTextField
              label="문항수"
              labelSx={{ typography: 's20w700' }}
              text={`${ipsativeRowCount}`}
              variant="outlined"
              name="ipsativeRowCount"
              stackSx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' } }}
            />
          </Grid>
          <Grid item xs={2} />
          <Grid item xs={5} md={3} sx={{ pl: '0px!important', pt: '0px!important' }}>
            <DialogTextField
              label="남은 문항수"
              labelSx={{ typography: 's20w700' }}
              text={`${remainCount}`}
              variant="outlined"
              name="ipsativeRemainRowCount"
              stackSx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' } }}
            />
          </Grid>
          <Box
            sx={{
              width: open ? `calc(100vw - 260px - 24px - 24px)` : 'calc(100vw - 24px -24px)',
              overflow: 'auto',
              position: 'absolute',
              bottom: '64px',
              maxHeight: 'calc(100vh - 350px)',
            }}
          >
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} justifyContent="space-evenly" sx={{ display: 'flex' }}>
                {drawGridCondition.map(obj => (
                  <Stack key={obj.id} direction="column" spacing={2} justifyContent="flex-end">
                    {Array.from(
                      { length: Number(dcInputs.stepper2[obj.id as keyof typeof dcInputs.stepper2]) },
                      (_, index) => (
                        <GridPointBox key={`${obj.id}-${index}`} width={90} height={40} />
                      ),
                    )}
                    <GridPoint
                      id={obj.id}
                      label={obj.label}
                      color={obj.color}
                      value={dcInputs.stepper2[obj.id as keyof typeof dcInputs.stepper2]}
                      width={90}
                      height={135}
                      typography="s36w800"
                    />
                  </Stack>
                ))}
              </Stack>
            </Grid>
          </Box>
        </Grid>
      </Box>
    </Grid>
  );
}

export default memo(StepperDC2);
